/**
 * WhatsApp's own text formatting, parsed the way WhatsApp Web renders it.
 *
 * Supported (matching WhatsApp's documented syntax):
 *   *bold*   _italic_   ~strikethrough~   `inline code`   ```monospace block```
 *   "> " quoted line,  "- "/"* " bulleted list,  "1. " numbered list
 *
 * The parser is deliberately separate from the renderer and free of JSX so its
 * behaviour can be exercised as plain data.
 *
 * Two rules do most of the work and come straight from WhatsApp:
 *  - an opening marker must be followed by a non-space, a closing marker
 *    preceded by one — so "2 * 3 * 4" and "a_b_c" stay literal;
 *  - an unmatched marker renders as the character itself, never as an empty tag.
 */

export type Inline =
  | { t: 'text'; v: string }
  | { t: 'b' | 'i' | 's'; c: Inline[] }
  | { t: 'code'; v: string }

export type Block =
  // `c` may contain newlines: a paragraph is parsed as one unit so a marker pair
  // can span lines (WhatsApp allows multi-line *bold*), while lists and quotes
  // stay line-based. That also bounds a stray marker's blast radius to its own
  // paragraph instead of the whole message.
  | { t: 'p'; c: Inline[] }
  | { t: 'quote'; c: Inline[] }
  | { t: 'ul'; items: Inline[][] }
  | { t: 'ol'; items: Inline[][]; start: number }
  | { t: 'pre'; v: string }

const MARKER: Record<string, 'b' | 'i' | 's'> = { '*': 'b', _: 'i', '~': 's' }

const isSpace = (c: string | undefined) => c === undefined || /\s/.test(c)

/**
 * A marker only opens a span when it is not glued to the end of a word and the
 * character after it starts one. `a_b_c` therefore stays literal, as on WhatsApp.
 */
function opens(text: string, i: number): boolean {
  const before = i > 0 ? text[i - 1] : undefined
  const after = text[i + 1]
  if (after === undefined || isSpace(after) || after === text[i]) return false
  return before === undefined || !/[\p{L}\p{N}]/u.test(before)
}

/** Index of the marker that closes the span opened at `open`, or -1. */
function closes(text: string, open: number): number {
  const ch = text[open]
  for (let j = open + 2; j < text.length; j++) {
    if (text[j] !== ch) continue
    if (isSpace(text[j - 1])) continue          // "* a *" — not a closer
    const after = text[j + 1]
    if (after !== undefined && /[\p{L}\p{N}]/u.test(after)) continue
    return j
  }
  return -1
}

/** Parses one line's inline markers. Code spans are literal — nothing nests inside. */
export function parseInline(text: string): Inline[] {
  const out: Inline[] = []
  let buf = ''
  const flush = () => { if (buf) { out.push({ t: 'text', v: buf }); buf = '' } }

  let i = 0
  while (i < text.length) {
    const ch = text[i]

    if (ch === '`') {
      const end = text.indexOf('`', i + 1)
      if (end > i + 1) {
        flush()
        out.push({ t: 'code', v: text.slice(i + 1, end) })
        i = end + 1
        continue
      }
    }

    const tag = MARKER[ch]
    if (tag && opens(text, i)) {
      const end = closes(text, i)
      if (end !== -1) {
        flush()
        out.push({ t: tag, c: parseInline(text.slice(i + 1, end)) })
        i = end + 1
        continue
      }
    }

    buf += ch
    i++
  }
  flush()
  return out
}

const QUOTE = /^>\s?(.*)$/
const BULLET = /^\s*[-*]\s+(.*)$/
const NUMBER = /^\s*(\d+)[.)]\s+(.*)$/

/** Groups consecutive lines of the same kind into blocks. */
function parseLines(text: string): Block[] {
  const blocks: Block[] = []
  const lines = text.split('\n')
  let i = 0

  while (i < lines.length) {
    const line = lines[i]

    const quote = QUOTE.exec(line)
    if (quote) {
      const acc: string[] = []
      while (i < lines.length) {
        const m = QUOTE.exec(lines[i])
        if (!m) break
        acc.push(m[1])
        i++
      }
      blocks.push({ t: 'quote', c: parseInline(acc.join('\n')) })
      continue
    }

    if (BULLET.test(line)) {
      const acc: Inline[][] = []
      while (i < lines.length) {
        const m = BULLET.exec(lines[i])
        if (!m) break
        acc.push(parseInline(m[1]))
        i++
      }
      blocks.push({ t: 'ul', items: acc })
      continue
    }

    const num = NUMBER.exec(line)
    if (num) {
      const start = Number(num[1])
      const acc: Inline[][] = []
      while (i < lines.length) {
        const m = NUMBER.exec(lines[i])
        if (!m) break
        acc.push(parseInline(m[2]))
        i++
      }
      blocks.push({ t: 'ol', items: acc, start })
      continue
    }

    const acc: string[] = []
    while (i < lines.length
      && !QUOTE.test(lines[i]) && !BULLET.test(lines[i]) && !NUMBER.test(lines[i])) {
      acc.push(lines[i])
      i++
    }
    blocks.push({ t: 'p', c: parseInline(acc.join('\n')) })
  }

  return blocks
}

/**
 * Splits the ``` fences out first: a monospace block is literal and may contain
 * newlines, so it must never be read as a list or a quote.
 */
export function parseWhatsAppText(text: string): Block[] {
  const blocks: Block[] = []
  let rest = text

  while (rest.length) {
    const open = rest.indexOf('```')
    if (open === -1) break
    const close = rest.indexOf('```', open + 3)
    if (close === -1) break

    if (open > 0) blocks.push(...parseLines(rest.slice(0, open)))
    // A leading newline right after the fence is the fence's own, not content.
    blocks.push({ t: 'pre', v: rest.slice(open + 3, close).replace(/^\n/, '').replace(/\n$/, '') })
    rest = rest.slice(close + 3)
  }

  if (rest.length) blocks.push(...parseLines(rest))
  return blocks
}

/** Plain text with every marker removed — for previews, which WhatsApp shows unformatted. */
export function stripWhatsAppMarkers(text: string): string {
  const flat = (nodes: Inline[]): string => nodes.map(n => {
    if (n.t === 'text') return n.v
    if (n.t === 'code') return n.v
    return flat(n.c)
  }).join('')

  return parseWhatsAppText(text).map(b => {
    if (b.t === 'pre') return b.v
    if (b.t === 'ul') return b.items.map(flat).join('\n')
    if (b.t === 'ol') return b.items.map(flat).join('\n')
    return flat(b.c)
  }).join('\n')
}
